/**
 * ShpfltQuickAdd — Quick add to cart button for product cards.
 *
 * Supports single-variant products (direct add) and multi-variant products
 * (shows variant selector popup). Integrates with existing cart drawer.
 *
 * @example
 * <shpflt-quick-add
 *   data-product-id="12345"
 *   data-product-handle="my-product"
 *   data-has-variants="true"
 *   data-first-variant-id="67890"
 *   data-product-available="true">
 *   <button ref="addButton">Add to cart</button>
 * </shpflt-quick-add>
 */

import { ShopigurusHTMLElement } from '@shopigurus/base';

class ShpfltQuickAdd extends ShopigurusHTMLElement {
  /** @type {number|null} Selected variant ID for multi-variant products */
  selectedVariantId = null;

  /** @type {Object|null} Cached product data for variant selection */
  productData = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindEvents();
  }

  /**
   * Binds click events to add button and variant options.
   */
  #bindEvents() {
    const { addButton, variantsClose, variantsAdd, variants } = this.refs;

    if (addButton) {
      addButton.addEventListener('click', this.#handleAddClick.bind(this), {
        signal: this.abortController.signal,
      });
    }

    if (variantsClose) {
      variantsClose.addEventListener('click', this.#closeVariants.bind(this), {
        signal: this.abortController.signal,
      });
    }

    if (variantsAdd) {
      variantsAdd.addEventListener('click', this.#handleVariantAdd.bind(this), {
        signal: this.abortController.signal,
      });
    }

    // Prevent clicks on popup background from bubbling to product link
    // But allow clicks on interactive elements (buttons) to work normally
    if (variants) {
      variants.addEventListener('click', (e) => {
        // Only prevent propagation if clicking the popup itself, not its children
        if (e.target === variants) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, {
        signal: this.abortController.signal,
      });
    }

    // Close variants on Escape key and trap focus
    this.addEventListener('keydown', this.#handleKeydown.bind(this), {
      signal: this.abortController.signal,
    });
  }

  /**
   * Handles click on the main add button.
   * @param {MouseEvent} e
   * @fires {cart:updated}
   */
  async #handleAddClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation(); // Prevent any other handlers

    const hasVariants = this.dataset.hasVariants === 'true';
    const productAvailable = this.dataset.productAvailable === 'true';

    if (!productAvailable) return;

    if (hasVariants) {
      const { addButton } = this.refs;
      if (!this.productData) {
        try {
          addButton?.setAttribute('aria-busy', 'true');
          const handle = this.dataset.productHandle;
          const response = await fetch(
            `${window.Shopify.routes.root}products/${handle}.js`,
            { signal: this.abortController.signal }
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch product data: ${response.status} ${response.statusText}`);
          }
          this.productData = await response.json();
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Failed to fetch product data:', error);
            this.#showError();
          }
          return;
        } finally {
          addButton?.setAttribute('aria-busy', 'false');
        }
      }

      const { options } = this.productData;
      if (options.length > 1) {
        window.location.href = `${window.Shopify.routes.root}products/${this.dataset.productHandle}`;
      } else {
        await this.#showVariants();
      }
    } else {
      const variantId = parseInt(this.dataset.firstVariantId, 10);
      await this.#addToCart(variantId);
    }
  }

  /**
   * Shows the variant selector popup.
   */
  async #showVariants() {
    const { variants } = this.refs;
    if (!variants || !this.productData) return;

    this.#renderVariantOptions();

    // Show variants popup
    variants.setAttribute('aria-hidden', 'false');

    // Focus first variant option
    const firstOption = variants.querySelector('.shpflt-quick-add__variant-option:not(:disabled)');
    firstOption?.focus();
  }

  /**
   * Renders variant options based on product data.
   */
  #renderVariantOptions() {
    const { variantOptionsContainer, variantsAdd } = this.refs;
    if (!variantOptionsContainer || !this.productData) return;

    const { variants } = this.productData;
    if (variantsAdd) {
      variantsAdd.hidden = false;
      variantsAdd.disabled = false;
    }
    variantOptionsContainer.innerHTML = '';

    for (const variant of variants) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'shpflt-quick-add__variant-option';
      button.textContent = variant.title;
      button.dataset.variantId = variant.id;
      button.setAttribute('aria-pressed', 'false');

      if (!variant.available) {
        button.disabled = true;
      }

      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.#selectVariant(variant.id, button);
      }, {
        signal: this.abortController.signal,
      });

      variantOptionsContainer.appendChild(button);
    }

    const firstAvailable = variants.find((v) => v.available);
    if (firstAvailable) {
      this.selectedVariantId = firstAvailable.id;
      const btn = variantOptionsContainer.querySelector(`[data-variant-id="${firstAvailable.id}"]`);
      btn?.setAttribute('aria-pressed', 'true');
    }
  }

  /**
   * Selects a variant and updates UI.
   * @param {number} variantId
   * @param {HTMLButtonElement} button
   */
  #selectVariant(variantId, button) {
    this.selectedVariantId = variantId;

    // Update aria-pressed state
    const allOptions = this.querySelectorAll('.shpflt-quick-add__variant-option');
    for (const opt of allOptions) {
      opt.setAttribute('aria-pressed', 'false');
    }
    button.setAttribute('aria-pressed', 'true');
  }

  /**
   * Handles click on variant popup add button.
   * @param {MouseEvent} e
   */
  async #handleVariantAdd(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!this.selectedVariantId) {
      this.#showError();
      return;
    }

    await this.#addToCart(this.selectedVariantId);
    this.#closeVariants();
  }

  /**
   * Adds item to cart via AJAX API.
   * @param {number} variantId
   * @fires {cart:updated}
   */
  async #addToCart(variantId) {
    const { addButton } = this.refs;
    if (!addButton) return;

    addButton.setAttribute('aria-busy', 'true');
    this.removeAttribute('data-error');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
        signal: this.abortController.signal,
      });

      const addResponse = await response.json();

      if (!response.ok) {
        const error = new Error(
          addResponse?.description || addResponse?.message || 'Failed to add to cart'
        );
        error.status = addResponse?.status || response.status;
        throw error;
      }
      const addedItem = addResponse;

      // Success state
      addButton.setAttribute('data-state', 'success');

      // Dispatch cart:updated event for cart drawer integration
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { variantId },
        })
      );

      // ALWAYS refresh cart drawer content and counter after add
      if (typeof window.cart !== 'undefined' && window.cart.getCart) {
        window.cart.getCart();
      }
      this.#refreshCartCount();

      // Handle post-add action based on setting
      const postAction = this.dataset.postAction || 'nothing';
      this.#handlePostAddAction(postAction, addedItem);

      // Reset button state after delay
      setTimeout(() => {
        addButton.removeAttribute('data-state');
      }, 2000);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Add to cart error:', error);
        this.#showError();
      }
    } finally {
      addButton.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * Shows error message.
   */
  #showError() {
    this.setAttribute('data-error', 'true');

    // Hide error after delay
    setTimeout(() => {
      this.removeAttribute('data-error');
    }, 3000);
  }

  /**
   * Handles post-add-to-cart action based on theme setting.
   * @param {string} action - Action type: 'nothing', 'cart_drawer', or 'notification'
   * @param {Object} item - Added item data from cart API
   */
  #handlePostAddAction(action, item) {
    switch (action) {
      case 'cart_drawer':
        // Open the cart drawer if it exists
        if (typeof window.cart !== 'undefined' && window.cart.openCartDropdown) {
          setTimeout(() => {
            window.cart.openCartDropdown();
          }, 400);
        }
        break;

      case 'notification':
        this.#showNotification(item);
        break;

      case 'nothing':
      default:
        // Do nothing - just show success state on button
        break;
    }
  }

  /**
   * Shows a toast-style notification for successful add to cart.
   * @param {Object} item - Added item data from cart API
   */
  #showNotification(item) {
    if (!item) return;

    const layout = this.dataset.notificationLayout || 'compact';
    const isMobile = window.innerWidth < 768;
    const positionVertical = isMobile ? (this.dataset.notificationPositionMobile || 'bottom') : (this.dataset.notificationPositionDesktop || 'bottom');
    const positionHorizontal = isMobile ? (this.dataset.notificationAlignMobile || 'center') : (this.dataset.notificationAlignDesktop || 'center');
    const borderRadius = this.dataset.notificationBorderRadius || '0';
    const bgColor = this.dataset.notificationBg || '#1a1a1a';
    const productTitle = item.product_title || item.title || this.dataset.addedText || 'Added to cart';
    const imageUrl = item.featured_image?.url || item.image || '';
    const quantity = item.quantity || 1;
    const priceText = this.#formatMoney(item.final_price ?? item.price ?? 0);
    const imageMarkup60 = imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" width="60" height="60">` : '';
    const imageMarkup80 = imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" width="80" height="80">` : '';
    const imageMarkup120 = imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" width="120" height="120">` : '';

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `shpflt-quick-add__notification shpflt-quick-add__notification--${layout}`;
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');
    notification.dataset.position = positionVertical;
    notification.dataset.align = positionHorizontal;
    notification.style.setProperty('--notification-border-radius', `${borderRadius}px`);
    notification.style.setProperty('--notification-bg', bgColor);

    // Build notification HTML based on layout
    let notificationHTML = '';

    switch (layout) {
      case 'compact':
        notificationHTML = `
          <div class="shpflt-quick-add__notification-image">
            ${imageMarkup60}
          </div>
          <div class="shpflt-quick-add__notification-info">
            <span class="shpflt-quick-add__notification-title">${productTitle}</span>
            <span class="shpflt-quick-add__notification-price">${priceText}</span>
          </div>
          <button type="button" class="shpflt-quick-add__notification-close" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
        break;

      case 'detailed':
        notificationHTML = `
          <div class="shpflt-quick-add__notification-image">
            ${imageMarkup80}
          </div>
          <div class="shpflt-quick-add__notification-info">
            <span class="shpflt-quick-add__notification-title">${productTitle}</span>
            ${this.#isVariantTitleVisible(item.variant_title) ? `<span class="shpflt-quick-add__notification-variant">${item.variant_title}</span>` : ''}
            <span class="shpflt-quick-add__notification-price">${priceText}</span>
            <span class="shpflt-quick-add__notification-qty">Qty: ${quantity}</span>
          </div>
          <button type="button" class="shpflt-quick-add__notification-close" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
        break;

      case 'image-first':
        notificationHTML = `
          <div class="shpflt-quick-add__notification-image">
            ${imageMarkup120}
          </div>
          <div class="shpflt-quick-add__notification-content">
            <div class="shpflt-quick-add__notification-info">
              <span class="shpflt-quick-add__notification-title">${productTitle}</span>
              ${this.#isVariantTitleVisible(item.variant_title) ? `<span class="shpflt-quick-add__notification-variant">${item.variant_title}</span>` : ''}
              <span class="shpflt-quick-add__notification-price">${priceText}</span>
            </div>
            <button type="button" class="shpflt-quick-add__notification-close" aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        `;
        break;

      case 'minimal':
        notificationHTML = `
          <svg class="shpflt-quick-add__notification-icon" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 8l4 4 8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div class="shpflt-quick-add__notification-info">
            <span class="shpflt-quick-add__notification-title">${productTitle}</span>
            <span class="shpflt-quick-add__notification-price">${priceText}</span>
          </div>
          <button type="button" class="shpflt-quick-add__notification-close" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
        break;

      default:
        notificationHTML = `
          <svg class="shpflt-quick-add__notification-icon" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 8l4 4 8-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>${this.dataset.addedText || 'Added to cart!'}</span>
        `;
    }

    notification.innerHTML = notificationHTML;

    // Append to body
    document.body.appendChild(notification);

    // Bind close button
    const closeBtn = notification.querySelector('.shpflt-quick-add__notification-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        notification.setAttribute('data-visible', 'false');
        setTimeout(() => notification.remove(), 300);
      });
    }

    // Trigger animation
    requestAnimationFrame(() => {
      notification.setAttribute('data-visible', 'true');
    });

    // Remove after delay
    setTimeout(() => {
      notification.setAttribute('data-visible', 'false');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 4000);
  }

  /**
   * Formats price in money format using Shopify money format.
   * Uses the shop's money format from window.theme.moneyFormat, which is
   * defined in layout/theme.liquid and respects the store's currency settings.
   *
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price with currency symbol
   */
  #formatMoney(cents) {
    const amount = Number(cents) || 0;
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';

    if (window.themeVendor?.themeCurrency?.formatMoney) {
      return window.themeVendor.themeCurrency.formatMoney(amount, moneyFormat);
    }

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount, moneyFormat);
    }

    const value = (amount / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', value);
  }

  /**
   * Refreshes cart count badges when cart drawer object is unavailable.
   */
  async #refreshCartCount() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) return;
      const cartData = await response.json();
      const count = cartData?.item_count ?? 0;
      const counters = document.querySelectorAll('[data-cart-count]');
      for (const counter of counters) {
        counter.textContent = count;
        counter.setAttribute('data-cart-count', count);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to refresh cart count:', error);
      }
    }
  }

  /**
   * Returns whether a variant title should be displayed in notification.
   * @param {string|null|undefined} variantTitle
   * @returns {boolean}
   */
  #isVariantTitleVisible(variantTitle) {
    if (variantTitle === null || variantTitle === undefined) return false;
    const normalized = String(variantTitle).trim().toLowerCase();
    return normalized !== '' && normalized !== 'default title' && normalized !== 'null' && normalized !== 'undefined';
  }

  /**
   * Closes the variant selector popup and restores focus.
   * @param {MouseEvent} [e] - Click event if triggered by close button
   */
  #closeVariants(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
    const { variants, addButton } = this.refs;
    if (variants) {
      variants.setAttribute('aria-hidden', 'true');
    }
    addButton?.focus();
  }

  /**
   * Handles keyboard navigation.
   * - Escape: closes the variant popup
   * - Tab/Shift+Tab: traps focus within the popup when open
   *
   * @param {KeyboardEvent} e
   */
  #handleKeydown(e) {
    const { variants } = this.refs;
    const isPopupOpen = variants?.getAttribute('aria-hidden') === 'false';

    // Close on Escape
    if (e.key === 'Escape' && isPopupOpen) {
      e.preventDefault();
      this.#closeVariants();
      return;
    }

    // Focus trap: cycle Tab/Shift+Tab within popup
    if (e.key === 'Tab' && isPopupOpen) {
      this.#trapFocus(e);
    }
  }

  /**
   * Traps focus within the variant popup dialog.
   * @param {KeyboardEvent} e
   */
  #trapFocus(e) {
    const { variants } = this.refs;
    if (!variants) return;

    // Get all focusable elements within the popup
    const focusableSelector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      '[tabindex="0"]',
    ].join(',');
    const focusableElements = Array.from(variants.querySelectorAll(focusableSelector));

    if (focusableElements.length === 0) return;

    const activeElement = this.ownerDocument.activeElement;
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Shift+Tab on first element: move to last
    if (e.shiftKey && activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
    }
    // Tab on last element: move to first
    else if (!e.shiftKey && activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  }
}

if (!customElements.get('shpflt-quick-add')) {
  customElements.define('shpflt-quick-add', ShpfltQuickAdd);
}
